class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.tabsContainer = this.querySelector('.product-tabs__container');
    this.tabsWrapper = this.querySelector('.product-tabs__wrapper');
    
    // Add a loading class that will be removed after initialization
    if (this.tabsWrapper) {
      this.tabsWrapper.classList.add('js-loading');
    }
    
    this.tabItems = Array.from(this.tabsWrapper.querySelectorAll('.product-tabs__item'));
    // Filter out empty tab items
    this.tabItems = this.tabItems.filter(item => !this.isTabEmpty(item));
    
    this.mobileBreakpoint = 767;
    this.isMobile = window.innerWidth <= this.mobileBreakpoint;
    this.activeIndex = 0; // Track the active tab/accordion
    
    // Hide all tab items initially to prevent flash of content
    this.tabItems.forEach(item => {
      item.style.display = 'none';
    });
    
    this.init();
    this.bindEvents();
  }
  
  // Check if a tab is empty (has no visible content)
  isTabEmpty(tabItem) {
    // Look for the content container
    const contentContainer = tabItem.querySelector('.product-tabs__item-content');
    if (!contentContainer) return true;
    
    // Check if the content container is empty
    const hasContent = contentContainer.textContent.trim() !== '' || 
                       contentContainer.children.length > 0;
    
    // If the content container is empty or only has whitespace, it's an empty tab
    return !hasContent;
  }

  init() {
    if (!this.tabItems.length) return;
    
    // Create tab navigation for desktop
    this.createTabNav();
    
    // Initialize appropriate view
    this.setViewMode();
    
    // Remove loading class after initialization
    if (this.tabsWrapper) {
      this.tabsWrapper.classList.remove('js-loading');
    }
  }
  
  bindEvents() {
    // Handle window resize to switch between tabs and accordion
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= this.mobileBreakpoint;
      
      // Only update if switching between mobile/desktop
      if (wasMobile !== this.isMobile) {
        this.setViewMode();
      }
    });
  }
  
  setViewMode() {
    if (this.isMobile) {
      this.setupAccordion();
    } else {
      this.setupTabs();
      // Activate the currently active tab
      this.activateTab(this.activeIndex);
    }
  }
  
  setupTabs() {
    // Show tab navigation
    this.tabsContainer.querySelector('.product-tabs__nav').style.display = 'flex';
    
    // Remove accordion headers if they exist
    this.tabItems.forEach(item => {
      const accordionHeader = item.querySelector('.product-tabs__accordion-header');
      if (accordionHeader) {
        accordionHeader.style.display = 'none';
      }
      
      // Reset any inline display styles from accordion mode
      Array.from(item.children).forEach(el => {
        if (!el.classList.contains('product-tabs__accordion-header')) {
          el.style.display = '';
        }
      });
      
      // Ensure proper display property for the tab item itself
      item.style.display = '';
    });
    
    // Add click events to tab buttons
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.activateTab(index));
    });
  }
  
  setupAccordion() {
    // Hide tab navigation
    this.tabsContainer.querySelector('.product-tabs__nav').style.display = 'none';
    
    // Create accordion headers if not already created
    this.tabItems.forEach((item, index) => {
      // Ensure the item itself is visible
      item.style.display = 'block';
      
      let accordionHeader = item.querySelector('.product-tabs__accordion-header');
      
      if (!accordionHeader) {
        // Get the tab title from the corresponding button
        const tabTitle = this.tabButtons[index].textContent;
        
        // Create accordion header
        accordionHeader = document.createElement('button');
        accordionHeader.className = 'product-tabs__accordion-header';
        accordionHeader.innerHTML = `<span>${tabTitle}</span><span class="product-tabs__accordion-icon"></span>`;
        accordionHeader.setAttribute('aria-expanded', 'false');
        accordionHeader.setAttribute('aria-controls', `tab-content-${index}`);
        
        // Insert before the content
        item.insertBefore(accordionHeader, item.firstChild);
        
        // Add click handler
        accordionHeader.addEventListener('click', () => this.toggleAccordion(index));
      } else {
        accordionHeader.style.display = 'flex';
      }
      
      // Reset hidden state for all accordion items
      item.hidden = false;
      
      // Initially hide all content except headers
      const content = Array.from(item.children).filter(el => !el.classList.contains('product-tabs__accordion-header'));
      content.forEach(el => el.style.display = 'none');
    });
    
    // Open the active accordion item
    this.toggleAccordion(this.activeIndex, true);
  }
  
  toggleAccordion(index, forceOpen = false) {
    const item = this.tabItems[index];
    const header = item.querySelector('.product-tabs__accordion-header');
    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    
    // Close all accordions first if opening a new one
    if (!isExpanded || forceOpen) {
      this.tabItems.forEach((otherItem, i) => {
        if (i !== index) {
          const otherHeader = otherItem.querySelector('.product-tabs__accordion-header');
          if (otherHeader) {
            otherHeader.setAttribute('aria-expanded', 'false');
            otherHeader.classList.remove('active');
            
            // Hide content
            const content = Array.from(otherItem.children).filter(el => !el.classList.contains('product-tabs__accordion-header'));
            content.forEach(el => el.style.display = 'none');
          }
        }
      });
    }
    
    // If already open and not forcing open, close it
    if (isExpanded && !forceOpen) {
      header.setAttribute('aria-expanded', 'false');
      header.classList.remove('active');
      
      // Hide content
      const content = Array.from(item.children).filter(el => !el.classList.contains('product-tabs__accordion-header'));
      content.forEach(el => el.style.display = 'none');
    } else {
      header.setAttribute('aria-expanded', 'true');
      header.classList.add('active');
      
      // Show content
      const content = Array.from(item.children).filter(el => !el.classList.contains('product-tabs__accordion-header'));
      content.forEach(el => el.style.display = 'block');
      
      // Update active index
      this.activeIndex = index;
    }
  }
  
  createTabNav() {
    // Create tab navigation container
    const tabNav = document.createElement('div');
    tabNav.className = 'product-tabs__nav';
    
    // Create buttons for each tab
    this.tabButtons = this.tabItems.map((tabItem, index) => {
      // Try to find tab title in the tab item container
      let tabTitle = 'Tab ' + (index + 1);
      
      // Look for the data-tab-title attribute in the item-heading div
      const itemHeading = tabItem.querySelector('.product-tabs__item-heading');
      if (itemHeading && itemHeading.dataset.tabTitle) {
        tabTitle = itemHeading.dataset.tabTitle;
      }
      
      const button = document.createElement('button');
      button.className = 'product-tabs__button';
      button.textContent = tabTitle;
      button.setAttribute('aria-controls', `tab-content-${index}`);
      button.setAttribute('role', 'tab');
      button.setAttribute('type', 'button');
      button.id = `tab-button-${index}`;
      
      tabNav.appendChild(button);
      return button;
    });
    
    // Insert tab navigation before tabs wrapper
    this.tabsContainer.insertBefore(tabNav, this.tabsWrapper);
    
    // Add IDs and roles to tab contents
    this.tabItems.forEach((item, index) => {
      item.id = `tab-content-${index}`;
      item.setAttribute('role', 'tabpanel');
      item.setAttribute('aria-labelledby', `tab-button-${index}`);
      item.hidden = true;
    });
  }
  
  activateTab(index) {
    // Hide all tabs
    this.tabItems.forEach((item, i) => {
      item.hidden = i !== index;
      
      // Ensure display is set appropriately
      if (i === index) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });
    
    // Update button states
    this.tabButtons.forEach((button, i) => {
      button.classList.toggle('active', i === index);
      button.setAttribute('aria-selected', i === index);
    });
    
    // Update active index
    this.activeIndex = index;
  }
}

customElements.define('product-tabs', ProductTabs); 